import { add } from '@/core/data/ecommerce';
import { active } from '@/core/data/uikits';
import { defineStore } from 'pinia';
import { getAllCondimentGroupAPI, addCondimentGroupAPI, deleteCondimentGroupAPI, updateCondimentGroupAPI, addCondimentAPI, getAllCondimentsByGroupAPI, updateCondimentAPI, deleteCondimentAPI } from '@/utils/api';

export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
}

export interface ViviposCondiments {
    MID: number;
    ID: string;
    CondimentGroupID?: string | null;
    Name?: string | null;
    Price?: number | null;
    ButtonColor?: string | null;
    FontSize?: string | null;
    Created?: number | null;
    Modified?: number | null;
    Preset?: boolean | null;
    AltLanguage?: string | null;
    PicThumb?: string | null;
}

export interface Condiment {
    id: string;
    name: string;
    price: number;
    preset: boolean;
    condimentGroupId?: string;
    appearance: Appearance;
    altDisplayLang: String[];
}

export function condimentToViviposCondiments(condiment: Condiment): ViviposCondiments {
    return {
        MID: 1,
        ID: condiment.id,
        CondimentGroupID: condiment.condimentGroupId ?? null,
        Name: condiment.name,
        Price: condiment.price,
        ButtonColor: condiment.appearance.buttonColor,
        FontSize: condiment.appearance.fontSizes,
        PicThumb: condiment.appearance?.image ?? '',
        Preset: condiment.preset,
        AltLanguage: condiment.altDisplayLang ? condiment.altDisplayLang.join('||') : ''
    };
}

export function viviposCondimentsToCondiment(vivipos: ViviposCondiments): Condiment {
    return {
        id: vivipos.ID,
        name: vivipos.Name ?? '',
        price: vivipos.Price ?? 0,
        preset: vivipos.Preset ?? false,
        condimentGroupId: vivipos.CondimentGroupID ?? '',
        appearance: {
            image: vivipos.PicThumb ?? '',
            buttonColor: vivipos.ButtonColor ?? '#dddddd',
            fontSizes: vivipos.FontSize ?? '',
        },
        altDisplayLang: vivipos.AltLanguage ? vivipos.AltLanguage.split('||') : [],
    };
}

export interface ViviposCondimentGroups {
    MID: number;
    ID: string;
    Name?: string | null;
    Created?: number | null;
    Modified?: number | null;
    Seltype?: string | null;
    Newline?: boolean | null;
}

export interface CondimentGroup {
    id: string;
    name: string;
    selectionType: string;
    newLine: boolean;
    condiments: Condiment[];
}

export function condimentGroupToViviposCondimentGroups(condimentGroup: CondimentGroup): ViviposCondimentGroups {
    return {
        MID: 1,
        ID: condimentGroup.id,
        Name: condimentGroup.name,
        Seltype: condimentGroup.selectionType,
        Newline: condimentGroup.newLine,
    };
}

export function viviposCondimentGroupsToCondimentGroup(vivipos: ViviposCondimentGroups): CondimentGroup {
    return {
        id: vivipos.ID,
        name: vivipos.Name ?? '',
        selectionType: vivipos.Seltype ?? '',
        newLine: vivipos.Newline ?? false,
        condiments: []
    };
}

export const defaultCondiment = {
    id: '',
    name: '',
    price: 0,
    preset: false,
    appearance: {
        image: '',
        buttonColor: '#dddddd',
        fontSizes: ''
    },
    altDisplayLang: []
} as Condiment;

export const defaultCondimentGroup = {
    id: '',
    name: '',
    selectionType: '',
    newLine: false,
    condiments: []
} as CondimentGroup;

export const useCondimentStore = defineStore('condiments', {
    state: () => ({
        condimentGroupsList: [] as CondimentGroup[],
        condimentList: [] as Condiment[],
        activeCondimentGroup: { ...defaultCondimentGroup },
        tempActiveCondimentGroup: { ...defaultCondimentGroup },
        activeCondiment: { ...defaultCondiment },
        tempActiveCondiment: { ...defaultCondiment },
    }),
    getters: {
        getActiveCondimentGroup: (state) => {
            return state.activeCondimentGroup;
        },
        getActiveCondimentGroupId: (state) => {
            return state.activeCondimentGroup.id;
        },
        findCondimentGroupById: (state) => (id: string) => {
            return state.condimentGroupsList.find(condimentGroup => condimentGroup.id === id);
        },
        getCondimentGroupsList: (state) => {
            return state.condimentGroupsList;
        },
        getTempActiveCondimentGroup: (state) => {
            return state.tempActiveCondimentGroup;
        },
        getIsActiveCondimentGroupChanged: (state) => {
            return JSON.stringify(state.activeCondimentGroup) !== JSON.stringify(state.tempActiveCondimentGroup);
        },
        getActiveCondiment: (state) => {
            return state.activeCondiment;
        },
        getTempActiveCondiment: (state) => {
            return state.tempActiveCondiment;
        },
        findCondimentById: (state) => (id: string) => {
            return state.activeCondimentGroup.condiments.find(condiment => condiment.id === id);
        },
        getActiveCondimentId: (state) => {
            return state.activeCondiment.id;
        }
    },
    actions: {
        initializeCondimentGroups() {
            getAllCondimentGroupAPI().then((response: CondimentGroup[]) => {
                this.condimentGroupsList = response;
            }).catch((error: Error) => {
                console.error('Error fetching condiment groups:', error);
            });
        },
        addCondimentGroup(condimentGroup: CondimentGroup) {
            addCondimentGroupAPI(condimentGroup).then((response) => {
                getAllCondimentGroupAPI().then((response: CondimentGroup[]) => {
                    this.condimentGroupsList = response;
                    this.activeCondimentGroup = condimentGroup;
                    this.tempActiveCondimentGroup = condimentGroup;
                }).catch((error: Error) => {
                    console.error('Error fetching condiment groups:', error);
                });
            }).catch((error) => {
                console.error('Error adding condiment group:', error);
            });
        },
        removeCondimentGroup(id: string) {
            deleteCondimentGroupAPI(id).then(() => {
                getAllCondimentGroupAPI().then((response: CondimentGroup[]) => {
                    this.condimentGroupsList = response;
                    this.activeCondimentGroup = defaultCondimentGroup;
                    this.tempActiveCondimentGroup = defaultCondimentGroup;
                }).catch((error: Error) => {
                    console.error('Error fetching condiment groups:', error);
                });
            }).catch((error) => {
                console.error('Error deleting condiment group:', error);
            });
        },
        updateCondimentGroup(updatedCondimentGroup: CondimentGroup) {
            updateCondimentGroupAPI(updatedCondimentGroup).then(() => {
                getAllCondimentGroupAPI().then((response: CondimentGroup[]) => {
                    this.condimentGroupsList = response;
                    this.activeCondimentGroup = defaultCondimentGroup;
                    this.tempActiveCondimentGroup = defaultCondimentGroup;
                }).catch((error: Error) => {
                    console.error('Error fetching condiment groups:', error);
                });
            }).catch((error) => {
                console.error('Error updating condiment group:', error);
            });
        },
        setActiveCondimentGroup(condimentGroup: CondimentGroup) {
            this.activeCondimentGroup = condimentGroup;
        },
        setTempActiveCondimentGroup(condimentGroup: CondimentGroup) {
            this.tempActiveCondimentGroup = condimentGroup;
        },
        initActiveCondimentGroup(condimentGroup: CondimentGroup) {
            getAllCondimentsByGroupAPI(condimentGroup.id).then((response: Condiment[]) => {
                this.activeCondimentGroup = condimentGroup;
                this.tempActiveCondimentGroup = condimentGroup;
                this.activeCondimentGroup.condiments = response.filter(condiment => condiment.condimentGroupId === condimentGroup.id);
            }).catch((error: Error) => {
                console.error('Error fetching condiment groups:', error);
            });
        },
        setActiveCondiment(condiment: Condiment) {
            this.activeCondiment = condiment;
        },
        setTempActiveCondiment(condiment: Condiment) {
            this.tempActiveCondiment = condiment;
        },
        addCondiment(condiment: Condiment) {
            condiment.condimentGroupId = this.activeCondimentGroup.id;
            addCondimentAPI(condiment).then((response) => {
                this.activeCondimentGroup.condiments.push(response);
                this.activeCondiment = response;
                this.tempActiveCondiment = response;
                getAllCondimentsByGroupAPI(this.activeCondimentGroup.id).then((response: Condiment[]) => {
                    this.activeCondimentGroup.condiments = response.filter(condiment => condiment.condimentGroupId === this.activeCondimentGroup.id);
                }).catch((error: Error) => {
                    console.error('Error fetching condiments:', error);
                });
            }).catch((error) => {
                console.error('Error adding condiment:', error);
            });
        },
        initActiveCondiment(condiment: Condiment) {
            this.activeCondiment = condiment;
            this.tempActiveCondiment = condiment;
        },
        updateCondiment(updatedCondiment: Condiment) {
            updateCondimentAPI(updatedCondiment).then(() => {
                this.activeCondimentGroup.condiments = this.activeCondimentGroup.condiments.map(condiment => condiment.id === updatedCondiment.id ? updatedCondiment : condiment);
                this.activeCondiment = updatedCondiment;
                this.tempActiveCondiment = updatedCondiment;
                getAllCondimentsByGroupAPI(this.activeCondimentGroup.id).then((response: Condiment[]) => {
                    this.activeCondimentGroup.condiments = response.filter(condiment => condiment.condimentGroupId === this.activeCondimentGroup.id);
                }).catch((error: Error) => {
                    console.error('Error fetching condiments:', error);
                });
            }).catch((error) => {
                console.error('Error updating condiment:', error);
            });
        },
        removeCondiment(id: string) {
            deleteCondimentAPI(id).then(() => {
                this.activeCondimentGroup.condiments = this.activeCondimentGroup.condiments.filter(condiment => condiment.id !== id);
                this.activeCondiment = defaultCondiment;
                this.tempActiveCondiment = defaultCondiment;
                getAllCondimentsByGroupAPI(this.activeCondimentGroup.id).then((response: Condiment[]) => {
                    this.activeCondimentGroup.condiments = response.filter(condiment => condiment.condimentGroupId === this.activeCondimentGroup.id);
                }).catch((error: Error) => {
                    console.error('Error fetching condiments:', error);
                });
            }).catch((error) => {
                console.error('Error deleting condiment:', error);
            });
        },
    },
});